import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../services/api';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  UserPlus,
  Mail,
  Phone,
  Building2,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Edit,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const statusColors = {
  new: 'badge-info',
  contacted: 'badge-warning',
  qualified: 'badge-success',
  unqualified: 'badge-gray',
  converted: 'badge-success'
};

const sourceColors = {
  website: 'bg-blue-100 text-blue-700',
  referral: 'bg-green-100 text-green-700',
  partner: 'bg-purple-100 text-purple-700',
  cold_outreach: 'bg-orange-100 text-orange-700',
  marketing: 'bg-pink-100 text-pink-700'
};

const abnStatusColors = {
  Active: 'bg-green-100 text-green-700',
  Cancelled: 'bg-red-100 text-red-700',
  Unverified: 'bg-yellow-100 text-yellow-700'
};

const marketingSyncStatusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  synced: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  opted_out: 'bg-gray-100 text-gray-700'
};

const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    abn: '',
    website: '',
    source: 'website',
    notes: '',
    abn: ''
  });
  const [abnLookupLoading, setAbnLookupLoading] = useState(false);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    source: searchParams.get('source') || ''
  });

  useEffect(() => {
    fetchLeads();
  }, [searchParams]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = {
        page: searchParams.get('page') || 1,
        search: searchParams.get('search') || '',
        status: searchParams.get('status') || '',
        source: searchParams.get('source') || '',
        limit: 20
      };

      const response = await adminApi.getLeads(params);
      setLeads(response.data.data || []);
      setPagination(response.data.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (err) {
      toast.error('Failed to load leads');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (filters.search) {
      newParams.set('search', filters.search);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(newPage));
    setSearchParams(newParams);
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error('Email is required');
      return;
    }
    try {
      await adminApi.createLead(formData);
      toast.success('Lead created successfully');
      setShowCreateModal(false);
      setFormData({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        abn: '',
        website: '',
        source: 'website',
        notes: '',
        abn: ''
      });
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create lead');
      console.error(err);
    }
  };

  const handleDeleteLead = async (lead) => {
    if (!confirm(`Are you sure you want to delete "${lead.companyName}"?`)) return;

    try {
      await adminApi.deleteLead(lead.id);
      toast.success('Lead deleted');
      fetchLeads();
    } catch (err) {
      toast.error('Failed to delete lead');
      console.error(err);
    }
  };

  const handleConvertToOpportunity = async (lead) => {
    try {
      await adminApi.convertLeadToOpportunity(lead.id);
      toast.success('Lead converted to opportunity');
      fetchLeads();
    } catch (err) {
      toast.error('Failed to convert lead');
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          <p className="text-gray-500 mt-1">Manage potential customers</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          New Lead
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-2xl font-bold text-gray-900">{pagination.total || 0}</p>
          <p className="text-sm text-gray-500">Total Leads</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-blue-600">{leads.filter(l => l.status === 'new').length}</p>
          <p className="text-sm text-gray-500">New This Week</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-green-600">{leads.filter(l => l.status === 'qualified').length}</p>
          <p className="text-sm text-gray-500">Qualified</p>
        </div>
        <div className="card p-4">
          <p className="text-2xl font-bold text-purple-600">{leads.filter(l => l.status === 'converted').length}</p>
          <p className="text-sm text-gray-500">Converted</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search leads..."
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                className="input pl-10"
              />
            </div>
          </form>

          <div className="flex gap-2">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input"
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="unqualified">Unqualified</option>
              <option value="converted">Converted</option>
            </select>

            <select
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
              className="input"
            >
              <option value="">All Sources</option>
              <option value="website">Website</option>
              <option value="referral">Referral</option>
              <option value="partner">Partner</option>
              <option value="cold_outreach">Cold Outreach</option>
              <option value="marketing">Marketing</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-header">Lead</th>
                <th className="table-header">Contact</th>
                <th className="table-header">ABN Status</th>
                <th className="table-header">Source</th>
                <th className="table-header">Status</th>
                <th className="table-header">Created</th>
                <th className="table-header text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8">
                    <div className="inline-flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mr-2"></div>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    No leads found
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <Link
                        to={`/sales/leads/${lead.id}`}
                        className="flex items-center group"
                      >
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                          <Building2 className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 group-hover:text-primary-600">
                            {lead.companyName}
                          </p>
                          <p className="text-xs text-gray-500">{lead.contactName}</p>
                        </div>
                      </Link>
                    </td>
                    <td className="table-cell">
                      <div className="space-y-1">
                        {lead.email && (
                          <div className="flex items-center text-sm text-gray-500">
                            <Mail className="w-3 h-3 mr-1" />
                            {lead.email}
                          </div>
                        )}
                        {lead.phone && (
                          <div className="flex items-center text-sm text-gray-500">
                            <Phone className="w-3 h-3 mr-1" />
                            {lead.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      {lead.abn ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            {lead.abnStatus === 'Active' && <CheckCircle className="w-3 h-3 text-green-600" />}
                            {lead.abnStatus === 'Cancelled' && <XCircle className="w-3 h-3 text-red-600" />}
                            {lead.abnStatus === 'Unverified' && <AlertCircle className="w-3 h-3 text-yellow-600" />}
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${abnStatusColors[lead.abnStatus] || 'bg-gray-100 text-gray-700'}`}>
                              {lead.abnStatus || 'Unknown'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500" title={lead.abnEntityName}>
                            {lead.abn}
                          </div>
                          {lead.gstRegistered && (
                            <div className="text-xs text-green-600">{lead.gstStatus || 'GST Registered'}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No ABN</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${sourceColors[lead.source] || 'bg-gray-100 text-gray-700'}`}>
                        {lead.source?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="table-cell">
                      <span className={statusColors[lead.status] || 'badge-gray'}>
                        {lead.status}
                      </span>
                    </td>
                    <td className="table-cell text-gray-500">
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </td>
                    <td className="table-cell text-right">
                      <div className="flex items-center justify-end gap-1">
                        {lead.status !== 'converted' && (
                          <button
                            onClick={() => handleConvertToOpportunity(lead)}
                            className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-green-50"
                            title="Convert to Opportunity"
                          >
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        )}
                        <Link
                          to={`/sales/leads/${lead.id}`}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDeleteLead(lead)}
                          className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 20 + 1} to{' '}
              {Math.min(pagination.page * 20, pagination.total)} of {pagination.total} leads
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-4 text-sm text-gray-700">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Lead Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create New Lead</h3>
            </div>

            <form onSubmit={handleCreateLead} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ABN
                  </label>
                  <input
                    type="text"
                    value={formData.abn}
                    onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                    className="input"
                    placeholder="12 345 678 901"
                  />
                  <p className="text-xs text-gray-500 mt-1">ABN status will be verified automatically</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="input"
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ABN (Australian Business Number)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.abn}
                    onChange={(e) => setFormData({ ...formData, abn: e.target.value.replace(/[^\d\s]/g, '') })}
                    className="input"
                    placeholder="00 000 000 000"
                    maxLength={14}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  ABN will be verified automatically when the lead is created
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source
                </label>
                <select
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  className="input"
                >
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="partner">Partner</option>
                  <option value="cold_outreach">Cold Outreach</option>
                  <option value="marketing">Marketing</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Lead
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPage;
